import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Film, Pencil, Plus, Trash2, type LucideIcon } from 'lucide-react';
import Tooltip from '../ui/Tooltip';

/**
 * The explorer's row primitives, lifted out of AssetExplorer so the container can stay about DATA and so the
 * section header stops being a component declared inside a render (which remounts its subtree on every keystroke —
 * fatal once a row holds a focused text input).
 *
 * Rename (RF3-EXPLORER D) lives here: double-click a row, or use its pencil, and the label becomes an input.
 * Enter commits, Escape reverts. The row itself never talks to a service — the container owns the optimistic
 * write and its revert.
 *
 * TITLES WRAP, THEY DO NOT TRUNCATE (owner 2026-08-23: "on left pane, allow word-wrap"). Rows used to clip at
 * the pane edge, so at the default 272px — and worse at the 224px minimum a dragged border allows — a longer
 * series or episode title ended in an ellipsis and could not be told apart from its neighbours. Two lines is
 * the cap: enough to read a real title in full at every pane width, while a pathological title still cannot
 * push the tree apart. A word with no break opportunity breaks rather than widening the pane.
 */
/**
 * Two lines of a wrapped title, then an ellipsis — shared so a row and its section header wrap identically.
 * The clamp goes on an inner span, never on the flex child itself: a flex item blockifies `-webkit-box` away
 * (measured in Chrome — the computed display comes back `flow-root`), which loses the ellipsis and clips the
 * third line mid-glyph instead of ending the second one cleanly.
 */
const WRAP = 'line-clamp-2 break-words leading-snug';

interface ActionProps {
  icon: LucideIcon;
  /** Announced to a screen reader — always names the row, never just the verb. */
  label: string;
  hint: string;
  onClick: () => void;
}

/**
 * A row's hover-revealed control (rename, delete). Extracted because the same button now appears four times —
 * two actions on an episode row and the same two on a section header — and every copy has to keep the same
 * bargain: invisible until the row is hovered, but `focus:opacity-100` so it comes back the instant it is
 * tabbed to. A control only a mouse can find is not a control.
 *
 * Monochrome even for delete: the warning belongs in the confirmation, which can afford words. A red icon on
 * every row would make a quiet file tree look alarming.
 */
export const RowAction: React.FC<ActionProps> = ({ icon: Icon, label, hint, onClick }) => (
  <Tooltip content={hint || label}>
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onDoubleClick={(e) => e.stopPropagation()}
      aria-label={label}
      className="shrink-0 rounded p-1 text-secondary-400 opacity-0 transition hover:text-secondary-700
                 focus:opacity-100 focus-visible:ring-1 focus-visible:ring-secondary-400
                 group-hover/row:opacity-100 dark:hover:text-white"
    >
      <Icon className="h-3 w-3" />
    </button>
  </Tooltip>
);

interface RenameProps {
  value: string;
  onCommit: (title: string) => void;
  onCancel: () => void;
  /** Announced on the input so a screen reader hears WHAT is being renamed, not just "edit text". */
  label: string;
}

/** The label, temporarily made editable in place. Committing a blank (or unchanged) title is a cancel. */
export const InlineRename: React.FC<RenameProps> = ({ value, onCommit, onCancel, label }) => {
  const [draft, setDraft] = useState(value);
  // Escape must not also fire the blur-commit on the way out, so the cancel is latched before focus moves.
  const cancelled = useRef(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.select(); }, []);

  const finish = () => {
    if (cancelled.current) return;
    const next = draft.trim();
    // An empty or whitespace-only title is never saved — a blank row is unreadable and unrecoverable.
    if (!next || next === value) onCancel(); else onCommit(next);
  };

  return (
    <input
      ref={ref}
      autoFocus
      value={draft}
      aria-label={label}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); finish(); }
        if (e.key === 'Escape') { e.preventDefault(); cancelled.current = true; onCancel(); }
      }}
      className="min-w-0 flex-1 rounded border border-secondary-300 bg-white px-1 py-0.5 text-[13px]
                 text-secondary-900 outline-none focus:border-secondary-500 focus:ring-1 focus:ring-secondary-400
                 dark:border-secondary-600 dark:bg-secondary-900 dark:text-white"
    />
  );
};

interface RowProps {
  label: string;
  sub?: string;
  depth?: number;
  active?: boolean;
  onClick: () => void;
  /** Present = this row can be renamed. Absent = no pencil, no double-click edit. */
  onRename?: (title: string) => void;
  renaming?: boolean;
  onRenameStart?: () => void;
  onRenameCancel?: () => void;
  /** Present = this row can be deleted. The container owns the confirmation and the optimistic removal. */
  onDelete?: () => void;
}

export const ExplorerRow: React.FC<RowProps> = ({
  label, sub, depth = 0, active, onClick, onRename, renaming, onRenameStart, onRenameCancel, onDelete,
}) => (
  <div
    style={{ paddingLeft: 8 + depth * 14 }}
    onDoubleClick={onRename ? () => onRenameStart?.() : undefined}
    className={`group/row flex w-full items-center gap-1 rounded-lg pr-1 transition-colors ${
      active
        ? 'bg-secondary-100 text-secondary-900 dark:bg-secondary-800 dark:text-white'
        : 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800'
    }`}
  >
    <Film className="h-3.5 w-3.5 shrink-0 text-secondary-400" />
    {renaming && onRename ? (
      <InlineRename
        value={label}
        label={`Rename ${label}`}
        onCommit={onRename}
        onCancel={() => onRenameCancel?.()}
      />
    ) : (
      <>
        {/* `aria-current` so "this is the row you are on" is carried by the ACCESSIBILITY tree and not only by a
            background colour — a screen-reader user gets the same orientation a sighted one does, and the
            section header beside it already announces itself the same way. */}
        <button
          type="button"
          onClick={onClick}
          aria-current={active ? 'page' : undefined}
          className="min-w-0 flex-1 py-1.5 text-left text-[13px]"
        >
          <span className={WRAP}>{label}</span>
        </button>
        {sub && <span className="shrink-0 font-mono text-[11px] text-secondary-400">{sub}</span>}
        {onRename && (
          <RowAction icon={Pencil} label={`Rename ${label}`} hint="Rename" onClick={() => onRenameStart?.()} />
        )}
        {onDelete && (
          <RowAction icon={Trash2} label={`Delete ${label}`} hint="Delete" onClick={onDelete} />
        )}
      </>
    )}
  </div>
);

interface SectionProps {
  label: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  onRename?: (title: string) => void;
  renaming?: boolean;
  onRenameStart?: () => void;
  onRenameCancel?: () => void;
  onDelete?: () => void;
  /** Add a child to this section — a new episode on a project's header (owner 2026-08-24). */
  onAdd?: () => void;
  addLabel?: string;
  /** OPEN this section's own subject (a project's Project page). Without it the label just expands. */
  onSelect?: () => void;
  /** The creator is somewhere INSIDE this section — its name reads brighter, but it is not the current row. */
  selected?: boolean;
  /**
   * The creator is on THIS section's own subject (the project page), so it takes the same filled treatment an
   * episode row gets when that episode is open. Distinct from `selected` on purpose: "inside this project" and
   * "on this project" are different places, and the pane has to say which (BUGS.md 2026-08-30).
   */
  active?: boolean;
  children: React.ReactNode;
}

/** A series (or the loose-videos bucket) and everything under it. Renameable when it is a real series. */
export const ExplorerSection: React.FC<SectionProps> = ({
  label, count, isOpen, onToggle, onRename, renaming, onRenameStart, onRenameCancel, onDelete, onAdd, addLabel,
  onSelect, selected, active, children,
}) => (
  <div className="mb-1">
    <div
      onDoubleClick={onRename ? () => onRenameStart?.() : undefined}
      className={`group/row flex w-full items-center gap-1.5 rounded-lg px-2 text-left text-[11px] font-semibold
                 uppercase tracking-wide ${
        active
          ? 'bg-secondary-100 text-secondary-900 dark:bg-secondary-800 dark:text-white'
          : 'text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-800'
      }`}
    >
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
        className="shrink-0 py-1.5">
        {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {renaming && onRename ? (
        <InlineRename value={label} label={`Rename ${label}`} onCommit={onRename} onCancel={() => onRenameCancel?.()} />
      ) : (
        <>
          {/* The NAME opens the project; the chevron beside it expands the episodes. Two jobs, two targets —
              a header that only expanded left the project's own page with no way in from the pane that lists
              it (owner 2026-08-24). Without `onSelect` (the loose-videos bucket) the name still just expands. */}
          <button
            type="button"
            onClick={onSelect || onToggle}
            // ONLY `active` is aria-current. `selected` means the creator is somewhere INSIDE this project, so
            // the name reads brighter as orientation — but the current row is the episode, and two elements
            // both claiming to be the current page tells a screen reader nothing.
            aria-current={active ? 'page' : undefined}
            className={`min-w-0 flex-1 py-1.5 text-left ${active || selected ? 'text-secondary-900 dark:text-white' : ''}`}
          >
            <span className={WRAP}>{label}</span>
          </button>
          <span className="font-mono">{count}</span>
          {onRename && (
            <RowAction icon={Pencil} label={`Rename ${label}`} hint="Rename" onClick={() => onRenameStart?.()} />
          )}
          {onAdd && (
            /* Add a child WHERE the children are (owner 2026-08-24) — a new episode belongs on its project's
               own header, not in a menu somewhere else that has to be told which project you meant. */
            <RowAction icon={Plus} label={addLabel || `Add to ${label}`} hint={addLabel || 'Add'} onClick={onAdd} />
          )}
          {onDelete && (
            <RowAction icon={Trash2} label={`Delete ${label}`} hint="Delete" onClick={onDelete} />
          )}
        </>
      )}
    </div>
    {isOpen && <div className="mt-0.5">{children}</div>}
  </div>
);
